// @flow

import * as React from 'react';
import {useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import Box from 'sail/Box';
import Group from 'sail/Group';
import Icon from 'sail/Icon';
import {DocsIcon} from 'docs/components';
import {repeat} from 'docs/util';
import {useAnalytics, useAccount, useLocation} from 'docs/data';
import {UnstyledLink} from 'sail/UnstyledLink';
import {CompactTooltip} from 'sail/Tooltip';
import {defineMessages, FormattedMessage} from 'react-intl';

import {Text} from 'sail/Text';

import {useToast} from 'docs/hooks';

import {renderCode} from './renderCode';
import {useCodeLineHeight} from './useCodeLineHeight';
import {useCodeTabGroupContext} from '../CodeTab/CodeTabGroup';
import {useStripeClui} from '../../contrib/StripeClui/helpers/react/provider';

import './CodeBlock.scss';

const messages = defineMessages({
  viewFullSample: {
    id: 'docsApp.components.codeBlock.viewFullSample',
    description: 'Label to expand preview and view full code sample',
    defaultMessage: 'View full sample',
  },
  seeAllLines: {
    id: 'docsApp.components.codeBlock.seeAllLines',
    description: 'Label to expand preview and view `count` more lines of code',
    defaultMessage: 'See all {count} lines',
  },
  hideLines: {
    id: 'docsApp.components.codeBlock.hideLines',
    description: 'Label to expand preview and view `count` more lines of code',
    defaultMessage: 'Hide lines',
  },
});

type LineNumberRange = {|
  start: number,
  end: number,
|};

export type CodeBlockHighlights = {|
  default?: Array<LineNumberRange>,
  add?: Array<LineNumberRange>,
  remove?: Array<LineNumberRange>,
|};

type CodeBlockHighlightedRangesProps = {|
  codeBlockRef: React.ElementRef<any>,
  highlights: CodeBlockHighlights,
|};

type CodeActionIconsProps = {|
  onCopy?: () => void,
  onRun?: () => void,
|};

const CodeActionIcons = ({onCopy, onRun}: CodeActionIconsProps) => (
  <Group direction="horizontal" spacing={12} alignItems="stretch">
    <UnstyledLink onClick={onCopy} title="Copy to clipboard">
      <Icon
        size={14}
        icon="clipboard"
        color="gray300"
        className="CodeBlock-action-icon"
      />
    </UnstyledLink>
    {onRun && (
      <CompactTooltip
        style={{display: 'inline-block'}}
        context={
          <UnstyledLink onClick={onRun} style={{display: 'block'}}>
            <Icon
              size={14}
              icon="playCircle"
              color="green"
              className="CodeBlock-action-icon"
            />
          </UnstyledLink>
        }
        label="Run with Stripe Shell"
      />
    )}
  </Group>
);

const CodeBlockHighlightedRanges = ({
  codeBlockRef,
  highlights,
}: CodeBlockHighlightedRangesProps) => {
  const {getHighlightStyle} = useCodeLineHeight(codeBlockRef);

  // Iterate through each set of highlights / diffs
  const highlightBlocks = Object.keys(highlights).reduce((blocks, type) => {
    if (!highlights[type]) {
      return blocks;
    }

    const ranges = highlights[type].map(({start, end}, index) => {
      return (
        <div
          key={`${type}-${index}`}
          className={classNames('CodeBlock-highlightedRange', type)}
          style={getHighlightStyle(start, end)}
        />
      );
    });

    return [...blocks, ...ranges];
  }, []);

  return highlightBlocks;
};

type CodeBlockLineNumbersProps = {|
  count: number,
|};

const CodeBlockLineNumbers = ({count}: CodeBlockLineNumbersProps) => {
  const lines = repeat(count, (index) => <span key={index} />);
  return <span className="CodeBlock-lineNumbers">{lines}</span>;
};

type CodeBlockHeaderProps = {|
  icon: string,
  githubSampleLink?: string,
  filename: string,
  onCopy?: () => void,
  onRun?: () => void,
|};

const CodeBlockHeader = ({
  filename,
  githubSampleLink,
  icon,
  onCopy,
  onRun,
}: CodeBlockHeaderProps) => {
  return (
    <Box className="CodeBlock-header" flex={{justifyContent: 'space-between'}}>
      <Group direction="horizontal" spacing={4} alignItems="center">
        {icon && (
          <Icon
            size={12}
            // $FlowExpectedError icon wants a specific string but we just have a generic one
            icon={icon}
            color="gray300"
            className="CodeBlock-file-icon"
          />
        )}
        {filename}
      </Group>
      <Group direction="horizontal" alignItems="center">
        {githubSampleLink && (
          <UnstyledLink
            href={githubSampleLink}
            title="View full sample on GitHub"
            className="CodeBlock-github-link"
          >
            <Group direction="horizontal">
              <Text className="CodeBlock-github-link-text">
                <FormattedMessage {...messages.viewFullSample} />
              </Text>
              <DocsIcon
                color="gray300"
                category="general"
                icon="github"
                className="CodeBlock-github-link-icon"
                size={16}
              />
            </Group>
          </UnstyledLink>
        )}
        <Box className={githubSampleLink && 'CodeBlock-header-left-border'}>
          <CodeActionIcons onCopy={onCopy} onRun={onRun} />
        </Box>
      </Group>
    </Box>
  );
};

type CodeBlockProps = {|
  className?: string,
  children: React.Node,
  filename?: string,
  link?: string,
  highlights?: CodeBlockHighlights,
  language?: string,
  icon?: string,
  id?: string,
  numbered?: boolean,
  maxLines?: number,
  onRun?: () => void,
|};

// TODO(ianjabour, 2022-01-10): Remove this once we roll this out to all pages...
const STRIPE_SHELL_SUPPORTED_PATHS = [
  '/docs/billing/prices-guide',
  '/docs/stripe-cli',
  '/docs/stripe-cli/test',
];

export const CodeBlock = ({
  className,
  children,
  filename,
  link,
  highlights,
  language,
  id,
  icon,
  numbered = true,
  maxLines,
  onRun,
}: CodeBlockProps) => {
  const {account} = useAccount();
  const {location} = useLocation();
  let prismLanguage = language;

  if (language === 'node') {
    prismLanguage = 'javascript';
  } else if (language === 'cli') {
    prismLanguage = 'bash';
  }

  const [hideLines, setHideLines] = useState(!!maxLines);
  const ref = useRef();
  const {trackAction} = useAnalytics();
  const [toast, showToast] = useToast();

  const {codeTabGroupCopyIconDiv, codeTabGroupID} = useCodeTabGroupContext();

  const {invokeCommand} = useStripeClui();

  const visibleCount = hideLines && maxLines ? maxLines : 0;
  const {code, count, plaintext} = useMemo(
    () => renderCode(children, prismLanguage, visibleCount),
    [children, prismLanguage, visibleCount],
  );

  const containerClasses = classNames('CodeBlock', className, {
    'CodeBlock-numbered': numbered,
  });

  const isTerminal = ['bash'].includes(prismLanguage);
  const hasHeader = filename || isTerminal;

  const handleCopy = () => {
    copy(plaintext);
    showToast('Copied!');
    trackAction('code_snippet_copy', {
      codeTabGroupID,
      lang: language,
    });
  };

  const cluiEnabled =
    account?.flags?.clui_v1_rollout &&
    STRIPE_SHELL_SUPPORTED_PATHS.includes(location.pathname);
  const isLangCLI =
    language === 'cli' ||
    (language === 'bash' && plaintext.startsWith('stripe '));

  let handleRun;
  if (cluiEnabled && isLangCLI) {
    handleRun = () => {
      trackAction('stripe_shell_snippet_execution', {
        command: plaintext,
        codeTabGroupID,
      });

      invokeCommand({command: plaintext});
    };
  }

  return (
    <Box
      id={id}
      background="blue800"
      className={containerClasses}
      data-language={prismLanguage}
    >
      {hasHeader ? (
        <CodeBlockHeader
          filename={filename || (isTerminal ? 'Terminal' : '')}
          icon={icon || (isTerminal ? 'api' : 'doc')}
          githubSampleLink={link}
          onCopy={handleCopy}
          onRun={handleRun}
        />
      ) : (
        codeTabGroupCopyIconDiv &&
        createPortal(
          <CodeActionIcons onCopy={handleCopy} onRun={handleRun} />,
          codeTabGroupCopyIconDiv,
        )
      )}
      <pre
        className={classNames('CodeBlock-content', {
          'CodeBlock-content-hasExtraLines': maxLines && hideLines,
        })}
      >
        <div className="CodeBlock-wrapper">
          <code ref={ref} className="CodeBlock-code">
            {code}
          </code>
          {numbered && <CodeBlockLineNumbers count={visibleCount || count} />}
          {highlights && (
            <CodeBlockHighlightedRanges
              highlights={highlights}
              codeBlockRef={ref}
            />
          )}
        </div>
      </pre>
      {maxLines && hideLines && (
        <UnstyledLink
          className="CodeBlock-extralines"
          onClick={() => {
            setHideLines(false);
          }}
        >
          <FormattedMessage {...messages.seeAllLines} values={{count}} />
        </UnstyledLink>
      )}
      {maxLines && !hideLines && (
        <UnstyledLink
          className="CodeBlock-extralines"
          onClick={() => {
            setHideLines(true);
          }}
        >
          <FormattedMessage {...messages.hideLines} />
        </UnstyledLink>
      )}
      {toast && <Box className="CodeBlock-toast">{toast}</Box>}
    </Box>
  );
};
